/**
 * useActivityLog Hook
 *
 * Hook for fetching activity logs for a contact in inbox-v3.
 * Groups activities by date with human-readable labels (Today, Yesterday, etc.)
 */

import {useMemo} from 'react'
import moment from 'moment'
import {useGetActivityLogByPhoneNumberQuery} from '../../../../../services/cellcast.v2'
import {ActivityLogItem, ActivityLogType} from '../../../../../services/cellcast.v2.types'

export interface UseActivityLogOptions {
  /** Phone number to fetch activity logs for */
  phoneNumber?: string
  /** Whether to enable the query */
  enabled?: boolean
  /** Page number for pagination */
  page?: number
  /** Limit for pagination */
  limit?: number
}

export interface GroupedActivityLog {
  /** Raw date string (YYYY-MM-DD) */
  date: string
  /** Human-readable date label (Today, Yesterday, Mar 28, 2026, etc.) */
  dateLabel: string
  /** Activity items for this date */
  items: ActivityLogItem[]
}

export interface ActivityIconConfig {
  icon: string
  className: string
  color: string
}

// Map activity types to icons and styles
export const ACTIVITY_TYPE_ICONS: Record<ActivityLogType, ActivityIconConfig> = {
  profile_updated: {
    icon: 'bi-person-gear',
    className: 'activity-profile',
    color: '#7239ea',
  },
  tag_added: {
    icon: 'bi-tag-fill',
    className: 'activity-tag-add',
    color: '#50cd89',
  },
  tag_removed: {
    icon: 'bi-tag',
    className: 'activity-tag-remove',
    color: '#f1416c',
  },
  message_sent: {
    icon: 'bi-send-fill',
    className: 'activity-message-sent',
    color: '#009ef7',
  },
  message_received: {
    icon: 'bi-chat-left-text-fill',
    className: 'activity-message-received',
    color: '#50cd89',
  },
  task_created: {
    icon: 'bi-check2-square',
    className: 'activity-task-created',
    color: '#ffc700',
  },
  task_completed: {
    icon: 'bi-check2-circle',
    className: 'activity-task-completed',
    color: '#50cd89',
  },
  note_added: {
    icon: 'bi-journal-text',
    className: 'activity-note',
    color: '#7239ea',
  },
  contact_created: {
    icon: 'bi-person-plus-fill',
    className: 'activity-contact-created',
    color: '#50cd89',
  },
  contact_deleted: {
    icon: 'bi-person-x-fill',
    className: 'activity-contact-deleted',
    color: '#f1416c',
  },
  subscription_changed: {
    icon: 'bi-bell-fill',
    className: 'activity-subscription',
    color: '#ffc700',
  },
  unknown: {
    icon: 'bi-activity',
    className: 'activity-unknown',
    color: '#a1a5b7',
  },
}

export interface UseActivityLogReturn {
  /** Grouped activity logs by date */
  groupedActivities: GroupedActivityLog[]
  /** Raw activity log items */
  activities: ActivityLogItem[]
  /** Whether activities are loading */
  isLoading: boolean
  /** Whether activities are being fetched */
  isFetching: boolean
  /** Error message if any */
  error: string | null
  /** Total count of activities */
  total: number
  /** Refetch activities */
  refetch: () => void
  /** Get icon config for an activity type */
  getActivityIcon: (type: ActivityLogType) => ActivityIconConfig
}

/**
 * Format date to human-readable label
 */
const formatDateLabel = (dateString: string): string => {
  const date = moment(dateString)
  const today = moment().startOf('day')
  const yesterday = moment().subtract(1, 'day').startOf('day')

  if (date.isSame(today, 'day')) {
    return 'Today'
  }
  if (date.isSame(yesterday, 'day')) {
    return 'Yesterday'
  }
  // Show day of week for last 7 days
  if (date.isAfter(moment().subtract(7, 'days'))) {
    return date.format('dddd') // e.g., "Monday"
  }
  // Show full date for older items
  return date.format('MMM D, YYYY') // e.g., "Mar 28, 2026"
}

/**
 * Group activities by date
 */
const groupActivitiesByDate = (activities: ActivityLogItem[]): GroupedActivityLog[] => {
  const grouped: Record<string, ActivityLogItem[]> = {}

  activities.forEach((activity) => {
    const date = moment(activity.sendTime || activity.createdAt).format('YYYY-MM-DD')
    if (!grouped[date]) {
      grouped[date] = []
    }
    grouped[date].push(activity)
  })

  // Convert to array and sort by date (newest first)
  return Object.entries(grouped)
    .map(([date, items]) => ({
      date,
      dateLabel: formatDateLabel(date),
      items: items.sort(
        (a, b) =>
          new Date(b.sendTime || b.createdAt).getTime() -
          new Date(a.sendTime || a.createdAt).getTime()
      ),
    }))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

export function useActivityLog(options: UseActivityLogOptions = {}): UseActivityLogReturn {
  const {phoneNumber, enabled = true, page = 1, limit = 50} = options

  // Clean phone number - remove country code prefix if present
  const cleanPhoneNumber = useMemo(() => {
    if (!phoneNumber) return ''
    // Remove leading + or country codes like 61
    return phoneNumber.replace(/^\+?61/, '').replace(/\D/g, '')
  }, [phoneNumber])

  // Fetch activity logs
  const {
    data: response,
    isLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useGetActivityLogByPhoneNumberQuery(
    {
      phoneNumber: cleanPhoneNumber,
      page,
      limit,
    },
    {
      skip: !enabled || !cleanPhoneNumber,
      refetchOnMountOrArgChange: true,
    }
  )

  // Extract activities from response
  const activities = useMemo(() => {
    if (!response?.data?.items) return []
    return response.data.items.map((item: ActivityLogItem) => ({
      ...item,
      // Ensure id is always set
      id: item.id || item._id,
      // Normalize activity type
      activityType: (item.activityType || 'unknown') as ActivityLogType,
    }))
  }, [response])

  // Group activities by date
  const groupedActivities = useMemo(() => {
    return groupActivitiesByDate(activities)
  }, [activities])

  // Error handling
  const error = useMemo(() => {
    if (queryError) {
      return 'Failed to load activity logs'
    }
    return null
  }, [queryError])

  // Total count
  const total = response?.data?.total || 0

  // Get icon config for activity type
  const getActivityIcon = (type: ActivityLogType): ActivityIconConfig => {
    return ACTIVITY_TYPE_ICONS[type] || ACTIVITY_TYPE_ICONS.unknown
  }

  return {
    groupedActivities,
    activities,
    isLoading,
    isFetching,
    error,
    total,
    refetch,
    getActivityIcon,
  }
}

export default useActivityLog